import { useState, FormEvent } from 'react';
import axios from 'axios';
import { redirectToOAuth, OAuthProvider } from '../utils/oauth';
import { useFallbackLogin } from '../api/hooks';
import { useAuth } from '../contexts/AuthContext';

function extractErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error) || !error.response) {
    return 'An unexpected error occurred. Please try again.';
  }

  const { status, data } = error.response;
  const serverMessage = typeof data?.message === 'string' ? data.message : null;

  if (status === 401) {
    return serverMessage ?? 'Invalid email or password.';
  }
  if (status === 403) {
    return (
      serverMessage ??
      'Fallback login is not available. Please use the Microsoft or Google sign-in options instead.'
    );
  }
  if (status === 423) {
    return serverMessage ?? 'This account is locked. Please contact your administrator.';
  }
  if (status === 429) {
    return 'Too many login attempts. Please wait a few minutes and try again.';
  }

  return serverMessage ?? 'Unable to sign in. Please try again.';
}

export function LoginPage() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const fallbackLogin = useFallbackLogin();
  const { refreshUser } = useAuth();

  const handleOAuthLogin = (provider: OAuthProvider) => {
    try {
      setError(null);
      setIsRedirecting(true);
      redirectToOAuth(provider);
    } catch (err) {
      setIsRedirecting(false);
      const message =
        err instanceof Error ? err.message : 'Unable to start login. Please try again.';
      setError(message);
      console.error(`OAuth login failed for ${provider}:`, err);
    }
  };

  const handleFallbackLogin = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      await fallbackLogin.mutateAsync({ email: email.trim(), password });
      await refreshUser();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ABPL</h1>
          <p className="text-gray-600">Sign in to continue</p>
        </div>

        {error ? (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : null}

        <div className="space-y-4">
          <button
            onClick={() => handleOAuthLogin('microsoft')}
            disabled={isRedirecting}
            className="w-full flex items-center justify-center gap-3 bg-[#2F2F2F] text-white py-3 px-4 rounded-md hover:bg-[#1F1F1F] focus:outline-none focus:ring-2 focus:ring-[#2F2F2F] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            {isRedirecting ? 'Redirecting...' : 'Sign in with Microsoft'}
          </button>

          <button
            onClick={() => handleOAuthLogin('google')}
            disabled={isRedirecting}
            className="w-full flex items-center justify-center gap-3 bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {isRedirecting ? 'Redirecting...' : 'Sign in with Google'}
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">Sign in with your corporate or personal account</p>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-6">
          <button
            type="button"
            aria-expanded={showFallback}
            disabled={fallbackLogin.isPending}
            onClick={() => {
              if (showFallback) {
                setError(null);
                setEmail('');
                setPassword('');
              }
              setShowFallback((prev) => !prev);
            }}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md py-2.5 px-4 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Sign in with email and password
            <svg
              className={`h-4 w-4 transition-transform ${showFallback ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showFallback ? (
            <form onSubmit={handleFallbackLogin} className="mt-4 space-y-4">
              <div>
                <label htmlFor="fallback-email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="fallback-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="fallback-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <input
                  id="fallback-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={fallbackLogin.isPending}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fallbackLogin.isPending ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </main>
  );
}
